import { delay } from "../client.js";

const STORAGE_KEY = "console-design.device-types.v1";

export const DEVICE_COLUMN_TYPES = ["string", "int", "file"];
export const DEVICE_TYPE_STATUSES = ["active", "deprecated"];

const SEED_DEVICE_TYPES = [
  {
    id: "dt-1",
    name: "GPS Tracker",
    manufacturer: "Intellicar",
    protocol: "TCP",
    status: "active",
    columns: [
      { key: "firmware_version", type: "string" },
      { key: "report_interval", type: "int" },
    ],
    createdAt: "2025-01-08T08:00:00.000Z",
    updatedAt: "2025-03-10T10:00:00.000Z",
  },
  {
    id: "dt-2",
    name: "Telematics Unit",
    manufacturer: "Intellicar",
    protocol: "MQTT",
    status: "active",
    columns: [{ key: "topic_prefix", type: "string" }],
    createdAt: "2025-01-12T09:00:00.000Z",
    updatedAt: "2025-01-12T09:00:00.000Z",
  },
  {
    id: "dt-3",
    name: "CAN Gateway",
    manufacturer: "BGauss",
    protocol: "CAN-over-IP",
    status: "active",
    columns: [{ key: "can_bitrate", type: "int" }],
    createdAt: "2025-02-01T09:00:00.000Z",
    updatedAt: "2025-02-01T09:00:00.000Z",
  },
  {
    id: "dt-4",
    name: "Legacy OBD",
    manufacturer: "Third Party",
    protocol: "OBD-II",
    status: "deprecated",
    columns: [],
    createdAt: "2024-06-01T09:00:00.000Z",
    updatedAt: "2025-01-01T09:00:00.000Z",
  },
];

function cloneSeed() {
  return SEED_DEVICE_TYPES.map((row) => ({
    ...row,
    columns: row.columns.map((c) => ({ ...c })),
  }));
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneSeed();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : cloneSeed();
  } catch {
    return cloneSeed();
  }
}

function saveStore(rows) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

function nextId() {
  return `dt-${Date.now().toString(36)}`;
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns)) return [];
  return columns
    .map((col) => ({ key: String(col.key ?? "").trim(), type: col.type }))
    .filter((col) => col.key && DEVICE_COLUMN_TYPES.includes(col.type));
}

function validatePayload(payload) {
  const name = payload.name?.trim();
  if (!name) throw new Error("Device type name is required");
  const manufacturer = payload.manufacturer?.trim();
  if (!manufacturer) throw new Error("Manufacturer is required");
  const protocol = payload.protocol?.trim();
  if (!protocol) throw new Error("Protocol is required");
  const status = payload.status?.trim();
  if (!DEVICE_TYPE_STATUSES.includes(status)) throw new Error("Select a valid status");
  const columns = normalizeColumns(payload.columns);
  const keys = new Set();
  for (const col of columns) {
    const lower = col.key.toLowerCase();
    if (keys.has(lower)) throw new Error(`Duplicate column key: ${col.key}`);
    keys.add(lower);
  }
  return { name, manufacturer, protocol, status, columns };
}

export async function listDeviceTypes() {
  await delay(120);
  return loadStore().map((row) => ({ ...row, columns: row.columns?.map((c) => ({ ...c })) ?? [] }));
}

export async function createDeviceType(payload) {
  await delay(140);
  const data = validatePayload(payload);
  const rows = loadStore();
  if (rows.some((r) => r.name.toLowerCase() === data.name.toLowerCase())) {
    throw new Error("A device type with this name already exists");
  }
  const now = new Date().toISOString();
  const row = { id: nextId(), ...data, createdAt: now, updatedAt: now };
  rows.push(row);
  saveStore(rows);
  return { ...row, columns: data.columns.map((c) => ({ ...c })) };
}

export async function updateDeviceType(deviceTypeId, payload) {
  await delay(120);
  const data = validatePayload(payload);
  const rows = loadStore();
  const row = rows.find((r) => r.id === deviceTypeId);
  if (!row) throw new Error("Device type not found");
  if (rows.some((r) => r.id !== deviceTypeId && r.name.toLowerCase() === data.name.toLowerCase())) {
    throw new Error("A device type with this name already exists");
  }
  Object.assign(row, data, { updatedAt: new Date().toISOString() });
  row.columns = data.columns.map((c) => ({ ...c }));
  saveStore(rows);
  return { ...row, columns: row.columns.map((c) => ({ ...c })) };
}

export async function deleteDeviceType(deviceTypeId) {
  await delay(100);
  const rows = loadStore();
  const index = rows.findIndex((r) => r.id === deviceTypeId);
  if (index < 0) throw new Error("Device type not found");
  rows.splice(index, 1);
  saveStore(rows);
}
